import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import GdsEventModel from '~/glm/models/gds/gds-event-model';
import GdsHouseholdModel from '~/glm/models/gds/gds-household-model';
import GdsUiState from '~/glm/models/gds/gds-ui-state';
import UserStore from '~/glm/models/user-store';
import NamesCell from '~/glm/components/guest-list/cells/names-cell';
import PartyOfCell from '~/glm/components/guest-list/cells/party-of-cell';
import ClickCell from '~/glm/components/guest-list/cells/click-cell';
import TextCell from '~/glm/components/guest-list/cells/text-cell.jsx';
import CheckboxCell from '~/glm/components/guest-list/cells/checkbox-cell.jsx';
import ContactInfoCellVariant from '~/glm/components/guest-list/cells/contact-info-cell-variant.jsx';
import RsvpsCell from '~/glm/components/guest-list/cells/rsvps-cell.jsx';
import MealsCell from '~/glm/components/guest-list/cells/meals-cell.jsx';
import RsvpDetailsCell from '~/glm/components/guest-list/cells/rsvp-details-cell.jsx';
import AnalyticsStore from '~/glm/models/analytics-store';
import SelectionCellGroup from '~/glm/components/guest-list-table/cells/selection-cell-group';
import cellStyles from './guest-row.scss';
import styles from './row.scss';

@observer
class HouseholdRow extends Component {
  static propTypes = {
    event: PropTypes.instanceOf(GdsEventModel).isRequired,
    userStore: PropTypes.instanceOf(UserStore).isRequired,
    gdsUiState: PropTypes.instanceOf(GdsUiState),
    rsvpEnabled: PropTypes.bool,
    household: PropTypes.instanceOf(GdsHouseholdModel).isRequired,
    editHousehold: PropTypes.func.isRequired,
    trackInviteSend: PropTypes.func.isRequired,
    trackThankYou: PropTypes.func.isRequired,
    trackMeal: PropTypes.func.isRequired,
    analyticsStore: PropTypes.instanceOf(AnalyticsStore),
    usesQuestions: PropTypes.bool.isRequired,
    onCopyFormLinkClick: PropTypes.func,
  };

  invitedGuests = () => {
    const { household, event } = this.props;
    return household.invitedGuests(event.id);
  };

  get partyCount() {
    return this.invitedGuests().length;
  }

  toggleMemberSelection = guest => {
    const curSelection = guest.selected;
    guest.setSelected(!curSelection);
  };

  get isSent() {
    const { event, household } = this.props;
    return household.anyGuestInvitationsSent(event.id);
  }

  get isThanked() {
    const { event, household } = this.props;
    return household.anyGuestThankYouSent(event.id);
  }

  get gift() {
    const { event, household } = this.props;
    return household.getGift(event.id);
  }

  toggleThankYou = eventId => {
    const { household, trackThankYou } = this.props;

    return Promise.all([household.toggleInvitedGuestsThankYouSent(eventId), trackThankYou(!this.isThanked)]);
  };

  renderSelectionCells = () => {
    const guests = this.invitedGuests();

    return <SelectionCellGroup group={guests} onClick={this.toggleMemberSelection} />;
  };

  render() {
    const {
      household,
      event,
      gdsUiState,
      trackMeal,
      editHousehold,
      analyticsStore,
      usesQuestions,
      rsvpEnabled,
      onCopyFormLinkClick,
    } = this.props;

    return (
      <tr key={`household-row-${household.id}`} className={styles.householdRow}>
        {this.renderSelectionCells()}
        <NamesCell onClick={() => editHousehold(household)} guests={this.invitedGuests()} />
        <ClickCell onClick={() => editHousehold(household)}>
          <PartyOfCell value={this.partyCount} />
        </ClickCell>
        <td>
          <ContactInfoCellVariant
            household={household}
            guests={this.invitedGuests()}
            onContactClick={() => editHousehold(household)}
            onCopyFormLinkClick={onCopyFormLinkClick}
          />
        </td>
        {
          <RsvpsCell
            gdsUiState={gdsUiState}
            analyticsStore={analyticsStore}
            invitations={household.guestInvitations(event.id)}
            guests={this.invitedGuests()}
          />
        }
        {gdsUiState.selectedEvent && gdsUiState.selectedEvent.hasMeals() && !usesQuestions && (
          <MealsCell
            mealQuestion={event.mealQuestion}
            analyticsStore={analyticsStore}
            trackMeal={trackMeal}
            gdsUiState={gdsUiState}
            invitations={household.guestInvitations(event.id)}
          />
        )}
        {gdsUiState.isColumnVisible('Notes') && (
          <ClickCell onClick={() => editHousehold(household)}>
            <TextCell type="notes" value={household.coupleNote} className={cellStyles.notes} />
          </ClickCell>
        )}
        {gdsUiState.isColumnVisible('Gift') && (
          <ClickCell onClick={() => editHousehold(household)}>
            <TextCell type="gift" value={household.getGift(event.id)} className={cellStyles.gift} />
          </ClickCell>
        )}
        {gdsUiState.isColumnVisible('Thank You Sent') && (
          <CheckboxCell
            entity={household}
            checked={household.anyGuestThankYouSent(event.id)}
            id={household.id}
            className={cellStyles.thankYou}
            onClick={this.toggleThankYou.bind(null, event.id)}
          />
        )}
        {gdsUiState.isColumnVisible('RSVP Details') && rsvpEnabled && (
          <RsvpDetailsCell
            id={household.id}
            onClick={() => {
              editHousehold(household);
              gdsUiState.setHouseholdFormDefaultState('responses');
            }}
            invitations={household.guestInvitations(event.id)}
          />
        )}
      </tr>
    );
  }
}

export default HouseholdRow;
